/**
 * Central Yes/No adapters for Contract Hub agreement fields.
 * Prisma YesNoEnum ('Yes' | 'No') ⇄ form radio ('yes' | 'no').
 */

export type YesNoFormValue = 'yes' | 'no';
export type YesNoApiValue = 'Yes' | 'No';

/** Null-safe normalize for comparisons / radio selection. */
export const normalizeYesNo = (value: unknown): string =>
  String(value ?? '').trim().toLowerCase();

/**
 * API / DB → form radio value.
 * Returns '' for null/empty; 'yes'/'no' for known booleans & YesNoEnum;
 * otherwise returns the trimmed string (non Yes/No fields pass through via callers).
 */
export const apiToFormYesNo = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'boolean') return value ? 'yes' : 'no';
  if (value === 1) return 'yes';
  if (value === 0) return 'no';

  const normalized = normalizeYesNo(value);
  if (normalized === 'yes' || normalized === 'true' || normalized === '1') return 'yes';
  if (normalized === 'no' || normalized === 'false' || normalized === '0') return 'no';

  return String(value).trim();
};

/**
 * Form radio → API YesNoEnum.
 * Empty → null. Known yes/no → 'Yes'/'No'.
 * Unrecognized values are preserved (with warn) to avoid silent DB overwrite.
 */
export const formToApiYesNo = (value: unknown): YesNoApiValue | null | string => {
  if (value === null || value === undefined || value === '') return null;

  const normalized = normalizeYesNo(value);
  if (normalized === 'yes' || normalized === 'true' || normalized === '1') return 'Yes';
  if (normalized === 'no' || normalized === 'false' || normalized === '0') return 'No';

  console.warn('[yesNoUtils] Unrecognized Yes/No value preserved as-is:', value);
  return String(value);
};

/** True when the normalized value is a known yes/no token. */
export const isYesNoFormValue = (value: unknown): value is YesNoFormValue => {
  const normalized = normalizeYesNo(value);
  return normalized === 'yes' || normalized === 'no';
};
